import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import { useTranslation } from 'react-i18next';
import ChartPlaceholder from './ChartPlaceholder';
import { createDualAxisGroupedConfig } from './chartConfigUtils';

interface SimulationData {
    readonly priceChangePercent: number;
    readonly estimatedNewCustomers: number;
    readonly estimatedNewContracts: number;
    readonly estimatedNewRevenueRon: number;
}

interface PriceSimulationChartProps {
    readonly data: readonly SimulationData[];
    readonly loading?: boolean;
    readonly currency?: 'EUR' | 'LEI';
}

function PriceSimulationChart({
    data,
    loading = false,
    currency = 'EUR',
}: PriceSimulationChartProps): JSX.Element {
    const { t } = useTranslation();

    if (loading || data.length === 0) {
        return <ChartPlaceholder loading={loading} hasData={data.length > 0} />;
    }

    const currSuffix = currency === 'EUR' ? 'EUR' : 'RON';
    const categories = data.map(
        d => `${d.priceChangePercent >= 0 ? '+' : ''}${d.priceChangePercent}%`,
    );
    const base = createDualAxisGroupedConfig(
        categories,
        t('statistics.bi.priceChange', 'Price Change'),
        t('statistics.bi.customers', 'Customers'),
        `${t('statistics.bi.revenue', 'Revenue')} (${currSuffix})`,
    );

    const options: Highcharts.Options = {
        ...base,
        series: [
            {
                type: 'column',
                name: t('statistics.bi.estimatedCustomers', 'Est. Customers'),
                data: data.map(d => d.estimatedNewCustomers),
                color: '#52c41a',
                yAxis: 0,
            },
            {
                type: 'column',
                name: t('statistics.bi.estimatedContracts', 'Est. Contracts'),
                data: data.map(d => d.estimatedNewContracts),
                color: '#1890ff',
                yAxis: 0,
            },
            {
                type: 'spline',
                name: `${t('statistics.bi.estimatedRevenue', 'Est. Revenue')} (${currSuffix})`,
                data: data.map(d => d.estimatedNewRevenueRon),
                color: '#fa541c',
                yAxis: 1,
                marker: {
                    enabled: true,
                },
            },
        ],
        credits: {
            enabled: false,
        },
    };

    return <HighchartsReact highcharts={Highcharts} options={options} />;
}

export default PriceSimulationChart;
